import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PartialRectIcon from '@mui/icons-material/GpsOff';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {focusItems, unfocusItems} from '@explorer/actions/focused-item';
import {
  quickFocusItems,
  quickUnfocusAllItems,
} from '@explorer/actions/quick-focused-item';
import {
  addItemsToOrder,
  addItemsToView,
  removeItemsFromOrder,
  removeItemsFromView,
} from '@explorer/actions/results';
import {trackEvent} from '@explorer/analytics';
import {
  FootprintToggleButton,
  OrderToggleButton,
  StyledBadge,
  ViewToggleButton,
} from '@explorer/components/items-panel/item-components';
import {
  IndicatorsContainer,
  InstrumentsIndicator,
  ItemIndicator,
} from '@explorer/components/ui/metadata-indicators';
import {UI_MODES} from '@explorer/constants';
import {formatInstrumentLabel} from '@explorer/util/filters';
import {getUniqueId} from '@explorer/util/items';
import {hasPartialRectification} from '@explorer/util/results';

const SceneListSubheader = ({
  group,
  resultId,
  groupIndex,
  canUserDownload,
  onExpand = () => {},
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const isComparing = useSelector(state => state.ui.mode === UI_MODES.COMPARE);
  const viewItems = useSelector(state => state.results.viewItems);
  const orderItems = useSelector(state => state.results.orderItems);
  const focusedItems = useSelector(state => state.focusedItems);
  const canUserDownloadAnyItems = useMemo(() => {
    const areAnyItemsAvailable = group.features.some(
      feature => feature.properties.available
    );

    return canUserDownload && areAnyItemsAvailable;
  }, [canUserDownload, group.features]);

  const [isExpanded, setIsExpanded] = useState(false);

  const featureIds = group.features.map(getUniqueId);
  const viewSelectedCount = featureIds.filter(id =>
    viewItems.includes(id)
  ).length;
  const anyViewSelected = viewSelectedCount > 0;

  const orderSelectedCount = featureIds.filter(id =>
    orderItems.includes(id)
  ).length;
  const anyOrderSelected = orderSelectedCount > 0;

  const footprintSelectedCount = group.features.filter(feature =>
    focusedItems.includes(feature)
  ).length;
  const anyFootprintSelected = footprintSelectedCount > 0;

  const {acquired, instrument, satellite_id, item_type} =
    group.features[0].properties;

  const onViewAllClick = () => {
    const payload = {
      items: featureIds,
      resultId,
    };

    if (anyViewSelected) {
      dispatch(removeItemsFromView(payload));
      trackEvent('Scene List: Hide strip on map', {
        itemCount: featureIds.length,
        itemType: item_type,
        groupNumber: groupIndex + 1,
      });
    } else {
      dispatch(addItemsToView(payload));
      trackEvent('Scene List: Show strip on map', {
        itemCount: featureIds.length,
        itemType: item_type,
        groupNumber: groupIndex + 1,
      });
    }
  };

  const onOrderAllClick = () => {
    const orderItemsIds = group.features
      .filter(feature => feature.properties.available)
      .map(getUniqueId);

    const payload = {
      items: orderItemsIds,
      resultId,
    };

    if (anyOrderSelected) {
      dispatch(removeItemsFromOrder(payload));
      trackEvent('Scene List: Remove strip from cart', {
        itemCount: featureIds.length,
        itemType: item_type,
        groupNumber: groupIndex + 1,
      });
    } else {
      dispatch(addItemsToOrder(payload));
      trackEvent('Scene List: Add strip to cart', {
        itemCount: featureIds.length,
        itemType: item_type,
        groupNumber: groupIndex + 1,
      });
    }
  };

  const onViewAllFootprintsClick = () => {
    const payload = {
      items: group.features,
    };

    if (anyFootprintSelected) {
      dispatch(unfocusItems(payload));
      trackEvent('Scene List: Hide strip footprint', {
        itemCount: featureIds.length,
        itemType: item_type,
        groupNumber: groupIndex + 1,
      });
    } else {
      dispatch(focusItems(payload));
      trackEvent('Scene List: Show strip footprint', {
        itemCount: featureIds.length,
        itemType: item_type,
        groupNumber: groupIndex + 1,
      });
    }
  };

  const onViewAllFootprintsFocus = () => {
    const payload = {
      items: group.features,
      reset: true,
    };

    dispatch(quickFocusItems(payload));
  };

  const onViewAllFootprintsBlur = () => {
    dispatch(quickUnfocusAllItems());
  };

  function handleExpandCollapse() {
    setIsExpanded(!isExpanded);
    onExpand();
    trackEvent(
      isExpanded ? 'Scene List: Collapse strip' : 'Scene List: Expand strip',
      {
        itemCount: featureIds.length,
        itemType: item_type,
        groupNumber: groupIndex + 1,
      }
    );
  }

  return (
    <Paper
      elevation={3}
      square
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        boxShadow: 'none',
        padding: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        ...theme.applyStyles('light', {
          backgroundColor: 'grey.100',
        }),
      })}
      data-qe={`scenes-grouphead-${satellite_id}`}
    >
      <IconButton
        onClick={handleExpandCollapse}
        aria-label={t(
          `sceneListGroupHeader.${
            isExpanded ? 'collapse' : 'expand'
          }SceneListSubheader`
        )}
      >
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
      <Box
        sx={{
          flex: 1,
          marginLeft: 1,
        }}
      >
        <Typography variant="body2" gutterBottom>
          <b>
            {t('common:time.short', {
              date: acquired,
            })}
          </b>{' '}
          {satellite_id && `- ${satellite_id}`}
        </Typography>
        <Box
          data-qe="item-count"
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 0.5,
          }}
        >
          {anyOrderSelected ? (
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontStyle: 'italic',
              }}
            >
              {t('sceneListGroupHeader.summaryItemsSelected', {
                count: orderSelectedCount,
              })}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {t('sceneListGroupHeader.summaryPromptAdd')}
            </Typography>
          )}
        </Box>
        <IndicatorsContainer>
          {instrument && (
            <InstrumentsIndicator label={formatInstrumentLabel(instrument)} />
          )}
          {hasPartialRectification(group.features) && (
            <ItemIndicator
              tooltip={t('sceneListGroupHeader.partiallyRectified')}
              icon={<PartialRectIcon />}
              data-qe="scene-group-partial-rectification-indicator"
            />
          )}
        </IndicatorsContainer>
      </Box>
      <Stack direction="row" spacing={1} sx={{marginRight: 0.5}}>
        <StyledBadge badgeContent={orderSelectedCount}>
          <Tooltip
            title={
              canUserDownloadAnyItems
                ? t(
                    `sceneListGroupHeader.${
                      anyOrderSelected
                        ? 'removeFromOrder_other'
                        : 'addToOrder_other'
                    }`,
                    {
                      count: group.features.length,
                    }
                  )
                : t('sceneListItem.orderNoPermissions')
            }
          >
            <OrderToggleButton
              disabled={!canUserDownloadAnyItems}
              selected={anyOrderSelected}
              value="orderItem"
              onClick={onOrderAllClick}
            />
          </Tooltip>
        </StyledBadge>
        <StyledBadge badgeContent={footprintSelectedCount}>
          <Tooltip
            title={
              anyFootprintSelected
                ? t('sceneListGroupHeader.removeFootprintFromMap_other', {
                    count: group.features.length,
                  })
                : t('sceneListGroupHeader.addFootprintToMap_other', {
                    count: group.features.length,
                  })
            }
          >
            <FootprintToggleButton
              selected={anyFootprintSelected}
              value="viewItemFootprint"
              onClick={onViewAllFootprintsClick}
              onMouseEnter={onViewAllFootprintsFocus}
              onFocus={onViewAllFootprintsFocus}
              onMouseLeave={onViewAllFootprintsBlur}
              onBlur={onViewAllFootprintsBlur}
            />
          </Tooltip>
        </StyledBadge>
        {!isComparing && (
          <StyledBadge badgeContent={viewSelectedCount}>
            <Tooltip
              title={
                anyViewSelected
                  ? t('sceneListGroupHeader.removeFromMap_other', {
                      count: group.features.length,
                    })
                  : t('sceneListGroupHeader.addToMap_other', {
                      count: group.features.length,
                    })
              }
            >
              <ViewToggleButton
                disabled={isComparing}
                selected={anyViewSelected}
                value="viewItem"
                onClick={onViewAllClick}
              />
            </Tooltip>
          </StyledBadge>
        )}
      </Stack>
    </Paper>
  );
};

SceneListSubheader.propTypes = {
  group: PropTypes.object.isRequired,
  resultId: PropTypes.string,
  onExpand: PropTypes.func,
  groupIndex: PropTypes.number.isRequired,
  canUserDownload: PropTypes.bool,
};

export default SceneListSubheader;
